import React, { forwardRef, RefObject, InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import KitErrorMessage from './KitErrorMessage';
import ThemeSchema from '../common/theme/schema';

export type KitSwitchProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
};

export const getLabelColor = (disabled: boolean, error: boolean, theme: ThemeSchema): string => {
  if (disabled) {
    return colors.gray_30;
  }
  if (error) {
    return theme.error;
  }
  return theme.label;
};

const getDefaultColor = (disabled: boolean, error: boolean, theme: ThemeSchema): string => {
  if (disabled) {
    return colors.gray_30;
  }
  if (error) {
    return theme.error;
  }
  return colors.gray_50;
};

const getCheckedColor = (disabled: boolean, error: boolean, theme: ThemeSchema): string => {
  if (disabled) {
    return colors.gray_30;
  }
  if (error) {
    return theme.error;
  }
  return colors.orange_50;
};

const StyledLabel = styled.label<{ disabled?: boolean; error?: boolean }>`
  && {
    font-family: ${({ theme }) => theme.font.fontFamily};
    font-size: ${constants.fontSize.text_14}rem;
    position: relative;
    padding-left: 2.1rem;
    display: flex;
    align-items: center;
    color: ${({ disabled, error, theme }) => getLabelColor(disabled, error, theme)};
  }

  & input {
    opacity: 0;
    width: 0;
    height: 0;
    transition: all 0.3s;
  }

  & .slider {
    transition: all 0.3s;
    position: absolute;
    box-sizing: border-box;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    background-color: ${colors.white};
    border: 1px solid ${({ disabled, error, theme }) => getDefaultColor(disabled, error, theme)};
    width: ${constants.spacing.xls}rem;
    height: ${constants.spacing.m}rem;
    left: 0;
  }

  & .slider:before {
    transition: transform 0.3s;
    position: absolute;
    content: '';
    top: 1px;
    left: 1px;
    width: calc(1rem - ${constants.spacing.absolute.xs}px);
    height: calc(1rem - ${constants.spacing.absolute.xs}px);
    background-color: ${({ disabled, error, theme }) => getDefaultColor(disabled, error, theme)};
    border-radius: 50%;
  }

  & input:checked + .slider {
    background-color: ${({ disabled, error, theme }) => getCheckedColor(disabled, error, theme)};
    border-color: ${({ disabled, error, theme }) => getCheckedColor(disabled, error, theme)};
  }

  & input:focus + .slider {
    outline-offset: 2px;
    outline: 2px solid ${({ theme }) => theme.focus};
  }

  & input:checked + .slider:before {
    transform: translateX(calc(${constants.spacing.m}rem - ${constants.spacing.absolute.xs}px));
    background-color: ${colors.white};
  }

  & .slider {
    border-radius: ${constants.spacing.s}rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const KitSwitch = forwardRef((props: KitSwitchProps, ref: RefObject<HTMLInputElement>) => {
  const { label, error, ...inputProps } = props;
  const labelOutput = props.required && label ? `${label} * ` : label;
  const errorMessageId = labelOutput
    ? `${labelOutput.split(' ').join('')}_error`
    : props['aria-describedby'];

  return (
    <Wrapper>
      <StyledLabel disabled={props.disabled} error={!!error}>
        <input
          {...inputProps}
          aria-required={props.required}
          type='checkbox'
          ref={ref}
          aria-invalid={!!error}
          aria-describedby={errorMessageId}
        />
        <span className='slider' />
        {labelOutput}
      </StyledLabel>
      {error && (
        <KitErrorMessage id={errorMessageId} isError={!!error}>
          {error}
        </KitErrorMessage>
      )}
    </Wrapper>
  );
});

export default KitSwitch;
